import * as React from 'react'
import moment from 'moment'
import { API, IComplianceAnalysis } from '@getgreenline/homi-shared'
import { inject, observer } from 'mobx-react'
import { CurrentCompanyStore } from '../../../../stores/CurrentCompanyStore'
import { LoadingAnimation } from '../../../../components/LoadingAnimation'
import { ComplianceStore } from './ComplianceStore'
import { message } from 'antd'
import { ProductListModal } from './ProductListModal'
import { parseErrorMsg } from '../../../../utilities/helpers'
import { BLAZE_COMPANY } from '../../../../constants/general'

interface Props {
  currentCompanyStore?: CurrentCompanyStore
  complianceStore: ComplianceStore
}

interface State {
  previousCompliance: IComplianceAnalysis[] | null
  compliance: IComplianceAnalysis[] | null
  showProductModal: boolean
  productModallist: IComplianceAnalysis[] | null
}

@inject('currentCompanyStore')
@observer
export class Status extends React.Component<Props, State> {
  rootRef: React.RefObject<HTMLDivElement>
  intersectionObserver: IntersectionObserver | null

  constructor(props: Props) {
    super(props)
    this.state = {
      previousCompliance: null,
      compliance: null,
      showProductModal: true,
      productModallist: null,
    }
    this.intersectionObserver = null
    this.rootRef = React.createRef()
  }

  async componentDidMount() {
    const { month, year, locationIds } = this.props.complianceStore
    const company = this.props.currentCompanyStore!.company!
    const userSelectedDate = moment(`${year}-${month}-01`)
    const previousMonth = moment(userSelectedDate).subtract(1, 'month').toDate()
    const currentMonth = moment(userSelectedDate).toDate()

    try {
      const [compliance, previousCompliance] = await Promise.all([
        API.getComplianceAnalysis(
          company.id,
          locationIds,
          currentMonth.getFullYear(),
          currentMonth.getMonth() + 1,
        ),
        API.getComplianceAnalysis(
          company.id,
          locationIds,
          previousMonth.getFullYear(),
          previousMonth.getMonth() + 1,
        ),
      ])

      this.setState({
        compliance,
        previousCompliance,
      })
    } catch (error) {
      message.error(parseErrorMsg(error) || 'There was an error loading your inventory status')
    }
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const { previousCompliance, compliance } = this.state

    if (
      !prevState.previousCompliance &&
      !prevState.compliance &&
      previousCompliance &&
      compliance &&
      'IntersectionObserver' in window
    ) {
      this.intersectionObserver = new IntersectionObserver(this.intersectionCallback, {
        root: null,
        rootMargin: '0px',
        threshold: 0,
      })

      if (this.rootRef.current) {
        this.intersectionObserver.observe(this.rootRef.current)
      }
    }
  }

  intersectionCallback(
    entries: IntersectionObserverEntry[],
    currentObserver: IntersectionObserver,
  ) {
    entries.forEach((entry: IntersectionObserverEntry) => {
      if (!entry.isIntersecting && !entry.intersectionRatio) {
        message.info('You have new alerts. Scroll up to see them')
      }
      currentObserver.disconnect()
    })
  }

  setShowProductModal(modalState: boolean, productlist: IComplianceAnalysis[] | null) {
    this.setState({
      showProductModal: modalState,
      productModallist: productlist,
    })
  }

  componentWillUnmount() {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect()
    }
  }

  get misMatchingStartEndProducts() {
    const misMatchingStartEndProducts: IComplianceAnalysis[] = []
    const { previousCompliance, compliance } = this.state

    if (!previousCompliance || !compliance) {
      return []
    }

    const mappedPrevCompliance = new Map<string, IComplianceAnalysis>()

    previousCompliance.forEach((pc) => {
      mappedPrevCompliance.set(pc.id, pc)
    })

    compliance.forEach((product) => {
      const matchingPrevComplianceProduct = mappedPrevCompliance.get(product.id)

      if (matchingPrevComplianceProduct) {
        const previousMonthEndingQuantity = matchingPrevComplianceProduct.closingInventory

        if (previousMonthEndingQuantity !== product.openingInventory) {
          misMatchingStartEndProducts.push(product)
        }
      }
    })

    return misMatchingStartEndProducts
  }

  render() {
    const { showProductModal, productModallist, previousCompliance, compliance } = this.state

    if (!previousCompliance || !compliance) {
      return <LoadingAnimation />
    }

    return (
      <div ref={this.rootRef}>
        {this.misMatchingStartEndProducts.length > 0 && (
          <div
            className='alert alert-danger mt-3 warning-box cursor-pointer'
            onClick={() => {
              const selection = document.getSelection()
              if (selection && selection.type !== 'Range') {
                this.setShowProductModal(true, this.misMatchingStartEndProducts)
              }
            }}
          >
            <span className='fa fa-exclamation-triangle mr-2' />
            We found {this.misMatchingStartEndProducts.length}{' '}
            {this.misMatchingStartEndProducts.length > 1 ? 'products' : 'product'} with mis-matching
            starting/ending inventories.
            <hr />
            If you notice issues, please contact {BLAZE_COMPANY} support.
          </div>
        )}
        {showProductModal && productModallist && (
          <ProductListModal
            onClose={() => this.setShowProductModal(false, null)}
            currentCompanyStore={this.props.currentCompanyStore}
            productList={productModallist}
          />
        )}
      </div>
    )
  }
}
